import { PRECISION } from '@App/app/configs/precision.config';
import { Annotation } from '@App/app/entities/layer/annotation.model';
import { CalculatedData } from '@App/app/entities/layer/calculated-data';
import { Cuboid } from '@App/app/entities/layer/cuboid.model';
import { LAYER_TYPES } from '@App/app/entities/layer/enums/layer-types.enum';
import { ModelsService } from '@App/app/pages/models/services/models-service/models.service';
import { Injectable } from '@angular/core';
import { Angle, BoundingBox, Color4, Mesh, Plane, Quaternion, Tools, Vector3 } from 'babylonjs';
import { BOX_TOTAL_VERTEX_COUNT, BOX_VERTICES_PER_FACE } from './rectangle-tools.constants';
import * as geomag from 'geomag';
import {
  ANNOTATION_NAME,
  CUBOID_OBJECT_NAME,
  ELECTRIC_BLUE_COLOR,
  NORTH_VECTOR,
} from '@App/app/configs/babylon.config';
import { BabylonNodesService } from '../../../babylon-nodes-service/babylon-nodes.service';
import { MaterialService } from '../../../material-service/material.service';
import { BasePlaneService } from '../../base-plane-services/base-plane.service';

@Injectable({
  providedIn: 'root',
})
export class VolumeUtilsService {
  constructor(
    private basePlaneService: BasePlaneService,
    private babylonNodesService: BabylonNodesService,
    private materialService: MaterialService,
    private modelsService: ModelsService,
  ) {}

  initBoundingBox(min: Vector3, max: Vector3, layer?: Cuboid | Annotation) {
    const name = layer?.type === LAYER_TYPES.CUBOID ? CUBOID_OBJECT_NAME : ANNOTATION_NAME;
    const boundingBox = new BoundingBox(min, max);
    const mesh = this.babylonNodesService.createBox(
      boundingBox.centerWorld.clone(),
      {
        width: boundingBox.extendSize.x * 2,
        height: boundingBox.extendSize.y * 2,
        depth: boundingBox.extendSize.z * 2,
      },
      null,
      `${name}${layer?.id}`,
      this.materialService.electricBlueMaterial,
    );

    mesh.renderingGroupId = 0;
    mesh.visibility = 0.6;
    mesh.enableEdgesRendering();
    mesh.edgesWidth = 0.8;
    mesh.edgesColor = Color4.FromHexString(`${ELECTRIC_BLUE_COLOR}FF`);
    return mesh;
  }

  getMeshVolume(mesh: Mesh) {
    const { maximum, minimum } = mesh.getBoundingInfo().boundingBox;
    const x = maximum.x - minimum.x;
    const y = maximum.y - minimum.y;
    const z = maximum.z - minimum.z;
    const volume = x * y * z * mesh.scaling.x * mesh.scaling.y * mesh.scaling.z;
    return +volume.toFixed(PRECISION.SPATIAL_MEASUREMENT_DECIMALS);
  }

  calculateDistanceToModelCenter(position1: Vector3, position2: Vector3) {
    const { x: x1, z: z1 } = position1;
    const { x: x2, z: z2 } = position2;
    const lengthXZ = Math.sqrt(Math.pow(z2 - z1, 2) + Math.pow(x2 - x1, 2)).toFixed(
      PRECISION.SPATIAL_MEASUREMENT_DECIMALS,
    );

    return +lengthXZ;
  }

  calculateHeightBetweenMeshes(position1: Vector3, position2: Vector3) {
    return +Math.abs(position1.y - position2.y).toFixed(PRECISION.SPATIAL_MEASUREMENT_DECIMALS);
  }

  calculateWorldDirectionAngle(position1: Vector3, position2: Vector3): number {
    const vec = position2.subtract(position1);
    vec.y = 0;
    const normalizedVec = Vector3.Normalize(vec);

    const dotProduct = Vector3.Dot(new Vector3(0, 0, 1), normalizedVec);
    let angle = Angle.FromRadians(Math.acos(dotProduct)).degrees();

    if (normalizedVec.x < position1.x) {
      angle = 360 - angle;
    }

    return +angle.toFixed(PRECISION.DISPLAY_ANGLE_DECIMALS);
  }

  calculateAzimuth(normal: Vector3) {
    const vec = normal.clone();
    vec.y = 0;

    const normalizedVec = Vector3.Normalize(vec);
    const dotVec = Vector3.Dot(this.magneticNorth() || NORTH_VECTOR, normalizedVec);
    const angleRadians = Math.acos(dotVec);
    let angleDegrees = Angle.FromRadians(angleRadians).degrees();

    // To make 0-360 scale, not only 0-180
    if (normalizedVec.x > 0) {
      angleDegrees = 360 - angleDegrees;
    }
    return +angleDegrees.toFixed(PRECISION.DISPLAY_ANGLE_DECIMALS);
  }

  calculateVerticalTilt(normal: Vector3) {
    const direction = new Vector3(normal.x, 0, normal.z);
    const dotVec = Vector3.Dot(direction, normal);
    const angleRadians = Math.acos(dotVec);
    const angleDegrees = Angle.FromRadians(angleRadians).degrees();

    const rounded = +angleDegrees.toFixed(PRECISION.DISPLAY_ANGLE_DECIMALS);
    return normal.y > 0 ? -rounded : rounded;
  }

  verticalTiltByMesh(box: Mesh) {
    const eulerAngles = box.absoluteRotationQuaternion.toEulerAngles();
    const degrees = Tools.ToDegrees(eulerAngles.x);
    return +(-degrees).toFixed(PRECISION.DISPLAY_ANGLE_DECIMALS);
  }

  getCalculatedData(mesh: Mesh): CalculatedData {
    const basePlanePosition = this.basePlaneService.getBasePlanesPosition();
    const onHeightDistance = this.calculateHeightBetweenMeshes(mesh.position, basePlanePosition);
    const modelCenterDistance = this.calculateDistanceToModelCenter(
      mesh.position,
      basePlanePosition,
    );
    const worldDirection = this.calculateWorldDirectionAngle(mesh.position, basePlanePosition);
    const { absoluteRotationQuaternion: quat } = mesh;
    const absoluteRotationQuaternion = { x: quat.x, y: quat.y, z: quat.z, w: quat.w };
    const color = '#fff';
    const clonedMesh = mesh.clone();
    clonedMesh.visibility = 0;
    clonedMesh.rotationQuaternion = new Quaternion(0, 0, 0, 0);
    const { min: minimum, max: maximum } = clonedMesh.getHierarchyBoundingVectors();
    const min = { x: minimum.x, y: minimum.y, z: minimum.z };
    const max = { x: maximum.x, y: maximum.y, z: maximum.z };
    clonedMesh.dispose();
    const volume = +((max.x - min.x) * (max.y - min.y) * (max.z - min.z)).toFixed(
      PRECISION.SPATIAL_MEASUREMENT_DECIMALS,
    );
    return {
      volume,
      absoluteRotationQuaternion,
      min,
      max,
      onHeightDistance,
      towerCenterDistance: modelCenterDistance,
      worldDirection,
      color,
    };
  }

  findPlanesTwoLargestSides(vertices: Vector3[]): [Plane, Plane] {
    const meshSidesVertices: [Vector3, Vector3, Vector3][] = [];
    for (let i = 0; i < BOX_TOTAL_VERTEX_COUNT; i += BOX_VERTICES_PER_FACE) {
      meshSidesVertices.push([vertices[i], vertices[i + 1], vertices[i + 2]]);
    }
    const perimeterSum = (v: [Vector3, Vector3, Vector3]) =>
      Vector3.Distance(v[0], v[1]) + Vector3.Distance(v[1], v[2]);
    meshSidesVertices.sort((a, b) => perimeterSum(b) - perimeterSum(a));
    return [Plane.FromPoints(...meshSidesVertices[0]), Plane.FromPoints(...meshSidesVertices[1])];
  }

  private magneticNorth() {
    let result: Vector3 | undefined;
    const { model } = this.modelsService;
    if (model?.location?.latitude && model?.location.longitude) {
      result = Vector3.Zero();
      const magneticDeclination = geomag.field(model.location.latitude, model.location.longitude)
        .declination;
      const declinationInDegrees = Tools.ToRadians(magneticDeclination);
      const q = Quaternion.FromEulerAngles(0, declinationInDegrees, 0);
      NORTH_VECTOR.rotateByQuaternionToRef(q, result);
    }
    return result;
  }
}
